<script setup>
/**
 * Global bottom-left toast. Driven entirely by `globalStore.toastNotification`;
 * prefer `globalStore.showToast(...)` / `dismissToast()` over poking the fields
 * directly (the actions own the auto-dismiss timer).
 *
 * Types: success · error · warning · progress · completed.
 * `progress` renders a spinner and never auto-dismisses — the caller replaces it
 * with `completed` or `error` when the queued job reports back (CDP-2459).
 */
import { computed } from 'vue'
import { useGlobalStore } from '@/stores/GlobalStore'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { TOAST_TYPES } from '@/constants'

const globalStore = useGlobalStore()

const toast = computed(() => globalStore.toastNotification)

// Per-type presentation. One lookup so a new type is one entry rather than a new
// ternary in three places.
//
// `tileClass` is the 32px rounded tint the icon sits in (Figma 28365:753889).
// It replaces the bare glyph: at 20px on a coloured surface the icon read as
// decoration, and the tile is what makes the toast's severity legible at a
// glance without relying on the border colour alone.
const TYPE_STYLES = {
  [TOAST_TYPES.SUCCESS]: {
    icon: 'heroicons:check-circle',
    iconClass: 'text-green-500',
    tileClass: 'bg-green-100',
    messageClass: 'text-gray-600',
    surfaceClass: 'bg-white border',
  },
  [TOAST_TYPES.COMPLETED]: {
    icon: 'heroicons:check-circle',
    iconClass: 'text-tint-1',
    tileClass: 'bg-blue-50',
    messageClass: 'text-tint-1 font-medium',
    surfaceClass: 'bg-white border',
  },
  [TOAST_TYPES.ERROR]: {
    icon: 'heroicons:exclamation-circle',
    iconClass: 'text-red-500',
    tileClass: 'bg-red-100',
    messageClass: 'text-red-600',
    surfaceClass: 'bg-red-50 border border-red-500',
  },
  [TOAST_TYPES.WARNING]: {
    icon: 'heroicons:exclamation-triangle',
    iconClass: 'text-yellow-500',
    tileClass: 'bg-yellow-100',
    // White surface, not a yellow wash: with the tint now carried by the icon
    // tile, colouring the card as well made a "name taken" read like an outage.
    messageClass: 'text-gray-400 font-medium',
    surfaceClass: 'bg-white border',
  },
  [TOAST_TYPES.PROGRESS]: {
    icon: '',
    iconClass: '',
    tileClass: '',
    messageClass: 'text-gray-500',
    surfaceClass: 'bg-white border',
  },
}

const style = computed(() => TYPE_STYLES[toast.value.type] ?? TYPE_STYLES[TOAST_TYPES.SUCCESS])
const isProgress = computed(() => toast.value.type === TOAST_TYPES.PROGRESS)

// Optional action button (e.g. "Refresh page"). Driven by
// `toastNotification.action = { label, icon, type, onClick }`. `type: 'reload'`
// reloads the page; an `onClick` callback runs (e.g. open a modal); otherwise the
// action just dismisses the toast.
const handleAction = () => {
  const action = toast.value.action
  globalStore.dismissToast()
  if (action?.type === 'reload') window.location.reload()
  else action?.onClick?.()
}
</script>

<template>
  <Transition name="fade">
    <!-- z above AppModal's 1999: a toast raised FROM a dialog (a duplicate tag
         name, say) otherwise renders behind its backdrop — invisible on desktop
         and completely covered by the full-height sheet on mobile. -->
    <section
      v-if="toast.show"
      class="fixed bottom-4 left-20 z-[2100] w-[372px] rounded-lg p-4 text-sm shadow max-md:left-4 max-md:w-[calc(100vw-2rem)]"
      :class="style.surfaceClass"
    >
      <div class="flex items-start gap-x-3">
        <div class="flex-1">
          <header v-if="toast.title" class="mb-1 flex flex-1 items-start justify-between">
            <div class="flex flex-1 items-center gap-x-2">
              <LoadingSpinner v-if="isProgress" width="20px" height="20px" class="shrink-0" />
              <span
                v-else
                class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                :class="style.tileClass"
              >
                <Icon :icon="style.icon" class="text-2xl" :class="style.iconClass" />
              </span>
              <h3 class="text-sm font-semibold text-gray-900">{{ toast.title }}</h3>
            </div>

            <button aria-label="Dismiss notification" @click="globalStore.dismissToast()">
              <Icon icon="heroicons:x-mark" class="text-base" />
            </button>
          </header>

          <div class="flex items-center gap-x-2">
            <p class="text-xs" :class="style.messageClass">{{ toast.message }}</p>

            <button
              v-if="!toast.title"
              aria-label="Dismiss notification"
              @click="globalStore.dismissToast()"
            >
              <Icon icon="heroicons:x-mark" class="text-base" />
            </button>
          </div>

          <button
            v-if="toast.action"
            class="mt-2 flex items-center gap-1 text-xs font-semibold text-tint-1 transition hover:text-tint-2"
            @click="handleAction"
          >
            <Icon v-if="toast.action.icon" :icon="toast.action.icon" class="text-sm" />
            {{ toast.action.label }}
          </button>
        </div>
      </div>
    </section>
  </Transition>
</template>
